import { GQL } from '@aline/graphql';

export interface CannedFieldMetadata {
  authorHelpText: string;
  buildTooltip: (defaultValue: string | null) => string;
  label: string;
}

interface AutoFilledTooltipArgs {
  authorSubject: string;
  manualEntrySubject: string;
}

function buildAutoFilledTooltip(
  args: AutoFilledTooltipArgs
): (defaultValue: string | null) => string {
  return defaultValue =>
    defaultValue
      ? `Will be set to ${args.authorSubject} (${defaultValue}) on signing`
      : `Signer will enter their ${args.manualEntrySubject}`;
}

/**
 * Variants whose value is filled automatically at signing time and cannot be
 * entered manually by the signer — DateSigned uses the signing timestamp;
 * UserEmail uses the signer's account email (WorkOS-authoritative identity).
 */
const AUTO_ONLY_CANNED_VARIANTS = [
  GQL.CannedFieldVariant.DateSigned,
  GQL.CannedFieldVariant.UserEmail,
] as const;

export function getIsAutoOnlyCannedVariant(variant: GQL.CannedFieldVariant): boolean {
  return (AUTO_ONLY_CANNED_VARIANTS as readonly GQL.CannedFieldVariant[]).includes(variant);
}

/**
 * Statuses that close a signer's canned value. `Completed` puts the value above
 * a signature that has already been given. `Declined` ends the signing session
 * outright — `finishRecipientFieldActions` refuses to finish a step holding a
 * declined recipient, and nothing resets the status afterwards, so there is no
 * ceremony left for them to fill a field in.
 */
const VALUE_CLOSED_RECIPIENT_STATUSES = [
  GQL.WorkflowRecipientStatus.Completed,
  GQL.WorkflowRecipientStatus.Declined,
] as const;

type ValueClosedRecipientStatus = (typeof VALUE_CLOSED_RECIPIENT_STATUSES)[number];

/**
 * Why the value is closed, phrased for the signer looking at the disabled input.
 * Typed against the status list above so a status cannot be closed without
 * saying why.
 */
const CLOSED_REASON_BY_STATUS: Record<ValueClosedRecipientStatus, string> = {
  [GQL.WorkflowRecipientStatus.Completed]: 'Already signed',
  [GQL.WorkflowRecipientStatus.Declined]: 'Signing declined',
};

/**
 * Is this canned field's value closed to further writes? Shared by the server's
 * `CannedFieldAuthorization.EditValue` check and by every input the UI offers,
 * so the two cannot drift into offering an edit the server rejects.
 *
 * Note: this reads the *assignee's* status, not the document's. A document is
 * only `isSigned` once every recipient has finished, so gating on that would
 * leave the input live for a signer who finished while a counterparty had not.
 */
export function getIsCannedValueClosed(
  assigneeStatus: GQL.WorkflowRecipientStatus | null | undefined
): boolean {
  if (!assigneeStatus) return false;
  return getIsValueClosedStatus(assigneeStatus);
}

export function getCannedValueClosedReason(
  assigneeStatus: GQL.WorkflowRecipientStatus | null | undefined
): string | null {
  if (!assigneeStatus || !getIsValueClosedStatus(assigneeStatus)) return null;
  return CLOSED_REASON_BY_STATUS[assigneeStatus];
}

function getIsValueClosedStatus(
  status: GQL.WorkflowRecipientStatus
): status is ValueClosedRecipientStatus {
  return VALUE_CLOSED_RECIPIENT_STATUSES.some(closedStatus => closedStatus === status);
}

function buildAutoOnlyTooltip(subject: string): (defaultValue: string | null) => string {
  return defaultValue =>
    defaultValue
      ? `Will be set to ${subject} (${defaultValue}) on signing`
      : `Will be set to ${subject} on signing`;
}

export const CANNED_FIELD_METADATA: Record<GQL.CannedFieldVariant, CannedFieldMetadata> = {
  [GQL.CannedFieldVariant.DateSigned]: {
    authorHelpText: 'Will be set to the current date on signing',
    buildTooltip: buildAutoOnlyTooltip('the current date'),
    label: 'Date Signed',
  },
  [GQL.CannedFieldVariant.UserName]: {
    authorHelpText: "Will be set to signer's name on signing",
    buildTooltip: buildAutoFilledTooltip({
      authorSubject: "signer's name",
      manualEntrySubject: 'name',
    }),
    label: 'Name',
  },
  [GQL.CannedFieldVariant.UserEmail]: {
    authorHelpText: "Will be set to signer's email on signing",
    buildTooltip: buildAutoOnlyTooltip("the signer's email"),
    label: 'Email',
  },
  [GQL.CannedFieldVariant.UserTitle]: {
    authorHelpText: "Will be set to signer's job title on signing",
    buildTooltip: buildAutoFilledTooltip({
      authorSubject: "signer's job title",
      manualEntrySubject: 'job title',
    }),
    label: 'Title',
  },
  [GQL.CannedFieldVariant.Address]: {
    authorHelpText: "Will be set to signer's address on signing",
    buildTooltip: buildAutoFilledTooltip({
      authorSubject: "signer's address",
      manualEntrySubject: 'address',
    }),
    label: 'Address',
  },
  [GQL.CannedFieldVariant.Company]: {
    authorHelpText: "Will be set to signer's company on signing",
    buildTooltip: buildAutoFilledTooltip({
      authorSubject: "signer's company",
      manualEntrySubject: 'company',
    }),
    label: 'Company',
  },
};
